(() => {
  'use strict';

  const ROOT_ATTR = 'data-molecular-circle-root';
  const ROOT_SELECTOR = `[${ROOT_ATTR}]`;

  // Track per-root instances so we can tear them down on section unload / re-init.
  const instances = new Map();

  const readPointNamePlural = () => {
    try {
      const node = document.getElementById('oke-loyalty-settings');
      const parsed = JSON.parse((node && node.textContent) || 'null');
      return (parsed && parsed.general && parsed.general.pointNamePlural) || 'Molecules';
    } catch (error) {
      return 'Molecules';
    }
  };

  class MolecularCircleShell {
    constructor(root) {
      this.root = root;
      this.statusNode = root.querySelector('[data-molecular-circle-status]');
      this.customerId =
        (window.ShopifyAnalytics &&
          window.ShopifyAnalytics.meta &&
          window.ShopifyAnalytics.meta.page &&
          window.ShopifyAnalytics.meta.page.customerId) ||
        null;
      this.pointNamePlural = readPointNamePlural();

      // Active retry timers (so they can be cleared on teardown).
      this.timers = new Set();

      this.actionNodes = [];
      this.onActionClick = this.onActionClick.bind(this);

      this.init();
    }

    setStatus(message = '') {
      if (this.statusNode) this.statusNode.textContent = message;
    }

    // Bounded retry against an Okendo API call, mirroring the referral deep-link
    // retry pattern (≈40 attempts at 300ms). Resolves true once invoked, false if
    // the API never becomes available within the window.
    retryApiCall(getFn, onReady) {
      if (typeof getFn() === 'function') {
        try {
          getFn().call(window);
          onReady();
          return true;
        } catch (error) {
          /* fall through to retry */
        }
      }

      let attempts = 0;
      const timer = setInterval(() => {
        const fn = getFn();
        if (typeof fn === 'function') {
          this.timers.delete(timer);
          clearInterval(timer);
          try {
            fn.call(window);
            onReady();
          } catch (error) {
            /* swallow — Okendo surfaces own their own errors */
          }
        } else if (++attempts > 40) {
          this.timers.delete(timer);
          clearInterval(timer);
        }
      }, 300);
      this.timers.add(timer);
      return false;
    }

    openLoyalty() {
      try {
        if (window.okeLoyaltyApi && typeof window.okeLoyaltyApi.open === 'function') {
          window.okeLoyaltyApi.open();
          this.setStatus('');
          return true;
        }
      } catch (error) {}

      // Fallback: bounded retry on okeLoyaltyApi.open() rather than clicking the
      // hidden launcher (which this page deliberately hides). Mirrors the referral
      // retry pattern.
      this.setStatus(
        'Rewards are loading. If the panel does not open, refresh the page and try again.'
      );
      return this.retryApiCall(
        () => window.okeLoyaltyApi && window.okeLoyaltyApi.open,
        () => this.setStatus('')
      );
    }

    openReferral() {
      try {
        if (window.okeReferralsApi && typeof window.okeReferralsApi.openModal === 'function') {
          window.okeReferralsApi.openModal();
          this.setStatus('');
          return true;
        }
      } catch (error) {}
      return this.openLoyalty();
    }

    onActionClick(event) {
      const node = event.currentTarget;
      const action = node.getAttribute('data-molecular-circle-action');
      if (!action) return;
      event.preventDefault();
      if (action === 'loyalty-open') this.openLoyalty();
      else if (action === 'referral-open') this.openReferral();
    }

    init() {
      if (this.customerId) {
        this.setStatus(
          `You are signed in. Open your rewards to see your live ${this.pointNamePlural.toLowerCase()} balance, tier, and referral link.`
        );
      } else {
        this.setStatus(
          `Join or sign in before checkout to earn ${this.pointNamePlural} on your next order.`
        );
      }

      this.actionNodes = Array.prototype.slice.call(
        this.root.querySelectorAll('[data-molecular-circle-action]')
      );
      this.actionNodes.forEach((node) => {
        node.addEventListener('click', this.onActionClick);
      });
    }

    destroy() {
      this.actionNodes.forEach((node) => {
        node.removeEventListener('click', this.onActionClick);
      });
      this.actionNodes = [];
      this.timers.forEach((timer) => clearInterval(timer));
      this.timers.clear();
    }
  }

  const destroyInstance = (root) => {
    const existing = instances.get(root);
    if (existing) {
      existing.destroy();
      instances.delete(root);
    }
  };

  const initInstance = (root) => {
    if (!root) return;
    // Re-init cleanly if this root was already wired (e.g. editor re-render).
    destroyInstance(root);
    instances.set(root, new MolecularCircleShell(root));
  };

  const initAll = (scope) => {
    const container = scope || document;
    if (container.matches && container.matches(ROOT_SELECTOR)) {
      initInstance(container);
    }
    container
      .querySelectorAll(ROOT_SELECTOR)
      .forEach((root) => initInstance(root));
  };

  // ---- Referral deep-link ---------------------------------------------------
  // /pages/molecular-circle?refer=1 (or ?referral=open) auto-opens the Okendo
  // referral modal once its API is available (bounded retry, ≈40 × 300ms).
  const handleReferralDeepLink = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('refer') !== '1' && params.get('referral') !== 'open') return;
      let attempts = 0;
      const timer = setInterval(() => {
        if (window.okeReferralsApi && typeof window.okeReferralsApi.openModal === 'function') {
          clearInterval(timer);
          window.okeReferralsApi.openModal();
        } else if (++attempts > 40) {
          clearInterval(timer);
        }
      }, 300);
    } catch (error) {}
  };

  // ---- Theme editor lifecycle ----------------------------------------------
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target && target.querySelector) {
      initAll(target);
    } else {
      initAll(document);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (!target || !target.querySelectorAll) return;
    if (target.matches && target.matches(ROOT_SELECTOR)) {
      destroyInstance(target);
    }
    target.querySelectorAll(ROOT_SELECTOR).forEach((root) => destroyInstance(root));
  });

  // ---- Boot ----------------------------------------------------------------
  const boot = () => {
    initAll(document);
    handleReferralDeepLink();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
